import { ColumnsType } from 'antd/lib/table';
import type { jsPDF as JsPDFType } from 'jspdf';
import type { UserOptions } from 'jspdf-autotable';
import { mainDateFormat } from './main-date-format';
import {
  getReportExportScopeText,
  getReportExportTableData,
  ReportExportMetric,
  ReportExportScope,
  toReportExportCellValue,
} from './report-export-table-data';

interface ExportReportToPdfParams<T> {
  chartsMode?: 'required' | 'none';
  chartsElement: HTMLElement | null;
  columns: ColumnsType<T>;
  fileName: string;
  metrics: ReportExportMetric[];
  reportTitle: string;
  rows: T[];
  scope: ReportExportScope;
}

type JsPdfWithAutoTable = JsPDFType & {
  autoTable: (options: UserOptions) => JsPdfWithAutoTable;
  lastAutoTable: { finalY: number };
};

const loadImageSize = (
  dataUrl: string,
): Promise<{ height: number; width: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = (): void =>
      resolve({ height: image.naturalHeight, width: image.naturalWidth });
    image.onerror = reject;
    image.src = dataUrl;
  });

export const exportReportToPdf = async <T>({
  chartsElement,
  chartsMode = 'required',
  columns,
  fileName,
  metrics,
  reportTitle,
  rows,
  scope,
}: ExportReportToPdfParams<T>): Promise<void> => {
  const [{ default: JsPDF }, { toPng }] = await Promise.all([
    import('jspdf'),
    import('html-to-image'),
  ]);

  await import('jspdf-autotable');

  const doc = new JsPDF({
    orientation: 'landscape',
  }) as JsPdfWithAutoTable;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const generatedAt = new Date();
  let cursorY = margin;

  doc.setFontSize(16);
  doc.text(reportTitle, margin, cursorY);
  cursorY += 6;

  doc.autoTable({
    body: [
      [getReportExportScopeText(scope)],
      ...(scope.asOfDate
        ? []
        : [
            [
              `Generated ${mainDateFormat(
                generatedAt,
              )} ${generatedAt.toLocaleTimeString()}`,
            ],
          ]),
    ],
    margin: { bottom: margin, left: margin, right: margin, top: margin },
    startY: cursorY,
    styles: { cellPadding: 1, fontSize: 9, overflow: 'linebreak' },
    theme: 'plain',
  });
  cursorY = doc.lastAutoTable.finalY + 6;

  const metricsBody = metrics.map(
    ({ caption, label, value }: ReportExportMetric) => [
      label,
      toReportExportCellValue(value),
      caption ?? '',
    ],
  );

  if (metricsBody.length) {
    doc.autoTable({
      body: metricsBody,
      head: [['Label', 'Value', 'Caption']],
      margin: { left: margin, right: margin },
      startY: cursorY,
      styles: { fontSize: 8 },
      theme: 'grid',
    });
    cursorY = doc.lastAutoTable.finalY + 8;
  }

  if (chartsMode === 'required' && !chartsElement?.children.length) {
    throw new Error('Report charts are unavailable.');
  }

  const chartImages = await Promise.all(
    Array.from(chartsMode === 'none' ? [] : chartsElement?.children ?? []).map(
      async element => {
        const chartsImage = await toPng(element as HTMLElement, {
          pixelRatio: 2,
        });
        const dimensions = await loadImageSize(chartsImage);

        return { chartsImage, ...dimensions };
      },
    ),
  );

  for (const { chartsImage, height, width } of chartImages) {
    const imageWidth = Math.min(
      contentWidth,
      ((pageHeight - margin * 2) * width) / height,
    );
    const imageHeight = (height / width) * imageWidth;

    if (cursorY + imageHeight > pageHeight - margin) {
      doc.addPage();
      cursorY = margin;
    }

    doc.addImage(chartsImage, 'PNG', margin, cursorY, imageWidth, imageHeight);
    cursorY += imageHeight + 8;
  }

  const { header, rows: tableRows } = getReportExportTableData<T>(
    columns,
    rows,
  );

  if (cursorY > pageHeight - margin - 20) {
    doc.addPage();
    cursorY = margin;
  }

  doc.autoTable({
    body: tableRows,
    head: [header],
    margin: { left: margin, right: margin },
    startY: cursorY,
    styles: {
      fontSize: 7,
      ...(chartsMode === 'none'
        ? { cellWidth: 38, overflow: 'linebreak' as const }
        : {}),
    },
    horizontalPageBreak: chartsMode === 'none',
    horizontalPageBreakRepeat: chartsMode === 'none' ? [0, 1] : undefined,
    rowPageBreak: chartsMode === 'none' ? 'avoid' : 'auto',
    theme: 'striped',
  });

  doc.save(fileName);
};
